/**
 * @fileoverview Authentication UI handlers for Smart Grind web application.
 *
 * This module provides functions for managing user authentication through Google OAuth,
 * including login, logout, UI updates, and error handling. It handles the popup-based
 * OAuth flow, session management, and switching between signed-in and local user modes.
 *
 * @module ui-auth
 */

import { UI_CONSTANTS } from './ui-constants';
import { GOOGLE_BUTTON_HTML } from './ui-constants';
import { state } from '../state';
import { data } from '../data';
import { api } from '../api';
import { showToast, hideEl } from '../utils';
import { app } from '../app';
import { renderers } from '../renderers';
import { openSigninModal } from './ui-modals';
import { ui } from './ui';

/**
 * Sets the loading state on authentication buttons.
 * @param {HTMLElement} button - The button element to update.
 * @param {boolean} loading - Whether to show loading state.
 * @param {string} [loadingText="Connecting..."] - Text to display while loading.
 */
export const setButtonLoading = (
    button: HTMLElement | null,
    loading: boolean,
    loadingText = 'Connecting...'
) => {
    if (!button) return;
    const btn = button as HTMLButtonElement;
    btn.disabled = loading;
    btn.innerHTML = loading ? loadingText : GOOGLE_BUTTON_HTML;
};

/**
 * Helper to reset all login buttons to default state
 */
const resetLoginButtons = () => {
    ui.setButtonLoading((state.elements['googleLoginButton'] as HTMLElement | null) ?? null, false);
    ui.setButtonLoading(
        (state.elements['modalGoogleLoginButton'] as HTMLElement | null) ?? null,
        false
    );
};

/**
 * Initiates Google OAuth login using a popup window.
 * Opens an authentication popup, listens for success/failure messages from the popup,
 * updates user session and UI on success, and handles errors or cancellations.
 *
 * Note: We do not poll the popup's closed property because Cross-Origin-Opener-Policy
 * (COOP) blocks access to popup properties when the popup navigates to cross-origin
 * pages (like Google OAuth). Instead, we rely on postMessage for completion and
 * a timeout for fallback.
 */
export const handleGoogleLogin = () => {
    showError(null);
    ui.setButtonLoading((state.elements['googleLoginButton'] as HTMLElement | null) ?? null, true);
    ui.setButtonLoading(
        (state.elements['modalGoogleLoginButton'] as HTMLElement | null) ?? null,
        true
    );

    // Open popup for auth
    const popup = window.open('/smartgrind/api/auth?action=login', 'auth', 'width=500,height=600');

    if (!popup) {
        // Popup blocked
        resetLoginButtons();
        showToast(
            'Sign-in popup was blocked. Please allow popups for this site and try again.',
            'error'
        );
        return;
    }

    let authCompleted = false;

    const handleAuthSuccess = async (authData: { userId: string; displayName: string }) => {
        const { userId, displayName } = authData;
        localStorage.setItem('userId', userId);
        localStorage.setItem('displayName', displayName);

        state.user.id = userId;
        state.user.displayName = displayName;
        const userDisplayEl = state.elements['userDisplay'] as HTMLElement | null;
        if (userDisplayEl) {
            userDisplayEl.innerText = displayName;
        }
        state.user.type = 'signed-in';
        localStorage.setItem(data.LOCAL_STORAGE_KEYS.USER_TYPE, 'signed-in');

        // Fetch token from secure endpoint for Service Worker
        // The HttpOnly cookie is automatically sent with this request
        try {
            const tokenResponse = await fetch('/smartgrind/api/auth?action=token', {
                credentials: 'include', // Send HttpOnly cookie
            });

            if (tokenResponse.ok) {
                const tokenData = await tokenResponse.json();
                if (tokenData.token) {
                    const { storeTokenForServiceWorker } = await import('../sw-auth-storage');
                    storeTokenForServiceWorker(tokenData.token).catch(() => {});
                }
            }
        } catch {
            // Token fetch failed - user is still authenticated via cookie
            // SW will fall back to cookie-based auth
        }

        api.loadData();
        updateAuthUI();

        // Close any open sign-in modals
        hideEl(state.elements['setupModal']);
        hideEl(state.elements['signinModal']);
    };

    const handleAuthFailure = (data: { message: string }) => {
        setTimeout(() => {
            showToast(`Sign-in failed: ${data.message}`, 'error');
        }, 100);
    };

    const cleanupAuth = () => {
        window.removeEventListener('message', messageHandler);
        resetLoginButtons();
    };

    // Listen for auth messages from popup
    const messageHandler = (event: MessageEvent): void | Promise<void> => {
        if (event.origin !== window.location.origin) {
            return;
        }
        authCompleted = true;
        clearTimeout(timeoutId);
        if (event.data.type === 'auth-success') {
            const p = handleAuthSuccess(event.data);
            p.finally(cleanupAuth);
            return p;
        }
        if (event.data.type === 'auth-failure') {
            handleAuthFailure(event.data);
        }
        cleanupAuth();
        return;
    };
    window.addEventListener('message', messageHandler);

    const handleAuthTimeout = () => {
        if (!authCompleted) {
            authCompleted = true;
            showToast('Sign-in timed out. Please try again.', 'error');
            cleanupAuth();
        }
    };

    // Timeout to reset buttons if no auth response received
    const timeoutId = setTimeout(handleAuthTimeout, UI_CONSTANTS.AUTH_TIMEOUT);
};

/**
 * Handles user logout or sign-in modal opening.
 * If the user is signed in, switches to local mode by clearing session data,
 * resetting state, and reloading local data. If local, opens the sign-in modal.
 */
export const handleLogout = async () => {
    if (state.user.type === 'signed-in') {
        // Switch to local user
        localStorage.removeItem('userId');
        localStorage.removeItem('displayName');
        const { clearTokenForServiceWorker } = await import('../sw-auth-storage');
        clearTokenForServiceWorker().catch(() => {});
        // Note: Cookie will be cleared by server or expire naturally
        state.user.id = null;

        // Switch to local user
        state.user.type = 'local';
        localStorage.setItem(data.LOCAL_STORAGE_KEYS.USER_TYPE, 'local');

        // Clear current data and reload local data
        state.clearProblems();
        state.clearDeletedIds();

        // Reset filter to 'all'
        state.ui.currentFilter = 'all';
        renderers.updateFilterBtns();

        // Initialize local user with fresh data
        app.initializeLocalUser();

        // Update the UI to show sign in option
        updateAuthUI();

        showToast('Switched to local mode');
    } else {
        // Open sign in modal for local users
        openSigninModal();
    }
};

/**
 * Updates the authentication UI elements based on the current user type.
 * Changes the disconnect button's text, icon, and title to reflect whether
 * the user is local (show sign-in) or signed-in (show sign-out).
 */
export const updateAuthUI = () => {
    const disconnectBtn = state.elements['disconnectBtn'] as HTMLElement | null;
    const isLocal = state.user.type === 'local';

    const signInIcon = `<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
    </svg>`;

    const signOutIcon = `<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
    </svg>`;

    if (disconnectBtn) {
        disconnectBtn.innerHTML = isLocal ? `${signInIcon} Sign In` : `${signOutIcon} Sign Out`;
        (disconnectBtn as HTMLElement).title = isLocal
            ? 'Sign in to sync across devices'
            : 'Sign out and switch to local mode';
    }
};

/**
 * Displays or hides setup error messages in the UI.
 * Shows an alert if a message is provided and toggles the visibility of the error element.
 * @param {string|null} msg - The error message to display, or null to hide the error.
 */
export const showError = (msg: string | null) => {
    showAuthError((state.elements['setupError'] as HTMLElement | null) ?? null, msg);
};

/**
 * Displays or hides sign-in error messages in the UI.
 * Shows an alert if a message is provided and toggles the visibility of the sign-in error element.
 * @param {string|null} msg - The error message to display, or null to hide the error.
 */
export const showAuthError = (element: HTMLElement | null, msg: string | null) => {
    if (element) {
        element.innerText = msg || '';
        element.classList.toggle('hidden', !msg);
    }
};

export const showSigninError = (msg: string | null) => {
    showAuthError((state.elements['signinError'] as HTMLElement | null) ?? null, msg);
};
